import { isNetworkError } from "@/Helpers/isNetworkError";
import { useOnce } from "@/Hooks/useOnce";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { useTrackingService } from "@/Services/TrackingService";
import { useAuthentication } from "@/Slices/authenticationSlice";
import type { TrackingInfo } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingInfo";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Loads the tracking updates for the public tracking page. A signed-in visitor
// who can see the shipment is sent to its full page instead.
export function useTrackingInfo(trId: string): TrackingInfo | undefined {
  const trackingService = useTrackingService();
  const [trackingInfo, setTrackingInfo] = useState<TrackingInfo | undefined>();

  const { checkAuthenticated } = useAuthentication();
  const navigate = useNavigate();
  const shipmentApi = useShipmentsApi();

  // A request that gets no answer at all is a console warning, not a Sentry
  // event: if it is the access check, a signed-in visitor stays on the public
  // view and the updates still load; if it is the updates, they stay empty.
  // Any other failure is rethrown so it still reaches Sentry.
  useOnce(async () => {
    const isAuthenticated = await checkAuthenticated();

    if (isAuthenticated) {
      const shipmentId = await findOwnShipmentId();
      if (shipmentId !== undefined) {
        navigate(`/view-shipment?shipmentId=${shipmentId}`);
        return;
      }
    }

    await loadTracking();
  });

  async function findOwnShipmentId(): Promise<string | undefined> {
    try {
      const response = await shipmentApi.swapTrackingIdForShipmentId({
        tId: trId,
      });
      return response.hasAccess && response.shipmentId
        ? response.shipmentId
        : undefined;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn("Could not check access to the shipment", { error });
        return undefined;
      }
      throw error;
    }
  }

  async function loadTracking() {
    try {
      setTrackingInfo(await trackingService.getTracking(trId));
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn("Could not load tracking updates", { error });
        return;
      }
      throw error;
    }
  }

  return trackingInfo;
}
